import React, { useCallback, useEffect, useRef, useState } from 'react';
import Pencil from 'lucide-react/dist/esm/icons/pencil';
import { Spinner, DatePicker } from '../../../../components/ui';
import { useUIStore } from '../../../../stores/uiStore';
import { getTranslation } from '../../../../i18n';
import { normalizeNumberInput } from './formatDate';

export type InlineCellType = 'text' | 'number' | 'date' | 'textarea';

type CellState = 'idle' | 'editing' | 'saving' | 'saved' | 'error';

// Module-level singleton: only one date cell may be open at a time.
let activeDateCellClose: (() => void) | null = null;

interface InlineCellProps {
  value: string | number | null;
  type?: InlineCellType;
  display: React.ReactNode;
  align?: 'left' | 'right';
  disabled?: boolean;
  onSave: (value: string) => Promise<void>;
}

/**
 * Fixed row height constant — matches the td h-11 (44px) set in TransactionList.
 * All states render inside this budget to prevent row-height shifting.
 */
const ROW_H = 'h-7'; // 28px inner — fits within 44px td with py-2

const InlineCell: React.FC<InlineCellProps> = ({
  value,
  type = 'text',
  display,
  align = 'left',
  disabled = false,
  onSave,
}) => {
  const { language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);
  const [state, setState] = useState<CellState>('idle');
  const [draft, setDraft] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const savingRef = useRef(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const originalValue = value == null ? '' : String(value);
  const isRight = align === 'right';

  const open = useCallback((e?: React.MouseEvent) => {
    if (disabled || state === 'saving') return;
    e?.stopPropagation();
    if (type === 'date' && activeDateCellClose) {
      activeDateCellClose();
    }
    setDraft(originalValue);
    setState('editing');
  }, [disabled, state, originalValue, type]);

  const close = useCallback(() => {
    setState('idle');
  }, []);

  useEffect(() => {
    if (state === 'editing' && type !== 'date') {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        if (inputRef.current instanceof HTMLInputElement) {
          inputRef.current.select();
        }
      });
    }
  }, [state, type]);

  // Register this date cell as the active one; clean up on unmount or state change.
  useEffect(() => {
    if (type === 'date' && state === 'editing') {
      activeDateCellClose = close;
      return () => {
        if (activeDateCellClose === close) activeDateCellClose = null;
      };
    }
  }, [type, state, close]);

  const flashSaved = useCallback(() => {
    setState('saved');
    clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setState('idle'), 900);
  }, []);

  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  const commit = useCallback(async (valueOverride?: string) => {
    if (savingRef.current) return;

    let trimmed = valueOverride !== undefined ? valueOverride : draft.trim();
    if (type === 'number') {
      if (trimmed === '') {
        // allow clearing
      } else {
        const normalized = normalizeNumberInput(trimmed);
        if (normalized == null) {
          setState('error');
          inputRef.current?.focus();
          return;
        }
        trimmed = normalized;
        setDraft(normalized);
      }
    }

    if (trimmed === originalValue) {
      close();
      return;
    }

    savingRef.current = true;
    setState('saving');
    try {
      await onSave(trimmed);
      savingRef.current = false;
      flashSaved();
    } catch {
      savingRef.current = false;
      setState('error');
      if (type !== 'date') inputRef.current?.focus();
    }
  }, [draft, originalValue, onSave, close, flashSaved, type]);

  useEffect(() => {
    if (state !== 'editing') return;

    const handlePointerDown = (e: PointerEvent) => {
      if (savingRef.current) return;
      const target = e.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        // For date cells, also ignore clicks inside the portal-rendered calendar dropdown
        if (type === 'date') {
          const portal = (target as Element).closest?.('[data-datepicker-portal]');
          if (portal) return;
        }
        if (type === 'date') {
          close();
        } else {
          commit();
        }
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [state, draft, type, close, commit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Enter') {
      if (type === 'textarea' && !e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      commit();
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      commit();
    }
  }, [type, commit, close]);

  const handleDateChange = useCallback((dateStr: string) => {
    setDraft(dateStr);
    commit(dateStr);
  }, [commit]);

  // ─── Editing / Saving / Error ───
  if (state === 'editing' || state === 'saving' || state === 'error') {
    const isBusy = state === 'saving';
    const hasError = state === 'error';

    if (type === 'date') {
      return (
        <div
          ref={containerRef}
          className={`flex items-center w-full ${isRight ? 'justify-end' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {isBusy ? (
            <Spinner size="sm" />
          ) : (
            <DatePicker
              value={draft}
              onChange={handleDateChange}
              placeholder={t('amlTransactionLabelSelectDate')}
              size="sm"
              className="w-full min-w-0"
            />
          )}
        </div>
      );
    }

    // ─── Text / Number / Textarea: single compact input, no buttons ───
    const borderCls = hasError
      ? 'border-status-error/60 focus:border-status-error'
      : 'border-subtle focus:border-brand/40';

    return (
      <div
        ref={containerRef}
        className={`flex items-center ${ROW_H} ${isRight ? 'justify-end' : ''} ${hasError ? 'animate-[shake_0.3s_ease-in-out]' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {isBusy ? (
          <Spinner size="sm" />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            inputMode={type === 'number' ? 'decimal' : undefined}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); if (hasError) setState('editing'); }}
            onKeyDown={handleKeyDown}
            disabled={isBusy}
            className={`
              w-full ${ROW_H} bg-surface-primary ${borderCls}
              border rounded-lg px-2 text-sm text-content outline-none
              placeholder-content-muted transition-colors
              ${isRight ? 'text-right' : 'text-left'}
            `}
            placeholder={type === 'number' ? '0.00' : t('amlInlineCellPlaceholderEnter')}
          />
        )}
      </div>
    );
  }

  const isSaved = state === 'saved';

  // ─── Disabled ───
  if (disabled) {
    return <span className={`inline-flex items-center w-full ${ROW_H}`}>{display}</span>;
  }

  // ─── Idle / Saved flash ───
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'F2') open(); }}
      className={`
        group/cell inline-flex items-center gap-1 w-full ${ROW_H} cursor-default select-none
        rounded-lg px-1 -mx-1
        transition-colors duration-700 ease-out
        ${isSaved ? 'bg-status-success/10' : 'bg-transparent hover:bg-brand/[0.06]'}
        ${isRight ? 'justify-end' : ''}
      `}
    >
      <span className="min-w-0 flex-1">{display}</span>
      <Pencil
        size={11}
        className={`shrink-0 text-content-faint transition-opacity duration-150 ${isSaved ? 'opacity-0' : 'opacity-0 group-hover/cell:opacity-100'}`}
      />
    </span>
  );
};

export default InlineCell;
